import type { ReactNode } from 'react';
import { Ban, Gauge, Lock, PlugZap, Timer } from 'lucide-react';
import { dateReunion } from '../../lib/useFormateurs';
import { texteCompteur, type EtatIa, type FonctionIa } from '../../lib/useUsageIa';

/**
 * Où en est la personne avec l'IA (TF-439, TF-474) : prestataire non raccordé,
 * IA désactivée par la direction — pour son compte ou sa formation, motif dit
 * —, pas encore ouverte à son compte (fermée de base), tuteur fermé à
 * l'apprenant qui passe un quiz, ou son compteur du jour face au quota que la
 * direction lui a accordé. La direction n'a pas de quota, donc pas de compteur.
 * L'API refuse de toute façon ce que l'écran annonce.
 */
export function BandeauEtatIa({ etat, fonction }: { etat: EtatIa; fonction: FonctionIa }): JSX.Element | null {
  const raccordee = etat.fonctions.find((ligne) => ligne.fonction === fonction)?.raccordee ?? false;
  const compteur = etat.compte;
  const formation = etat.formationApprenant;

  if (!raccordee) {
    return (
      <Bandeau ton="info" icone={<PlugZap className="h-5 w-5" aria-hidden />}>
        Aucun prestataire d’IA n’est raccordé à la plateforme : cette fonction sera disponible une fois le prestataire
        choisi.
      </Bandeau>
    );
  }
  if (compteur?.desactivee) {
    return (
      <Bandeau ton="danger" icone={<Ban className="h-5 w-5" aria-hidden />}>
        L’IA est désactivée pour votre compte par la direction depuis le {dateReunion(compteur.desactivee.le)}. Motif :{' '}
        {compteur.desactivee.motif}
      </Bandeau>
    );
  }
  if (formation?.desactivee) {
    return (
      <Bandeau ton="danger" icone={<Ban className="h-5 w-5" aria-hidden />}>
        L’IA est désactivée pour votre formation « {formation.titre} » par la direction. Motif :{' '}
        {formation.desactivee.motif}
      </Bandeau>
    );
  }
  if (compteur?.etat === 'NON_AUTORISE') {
    return (
      <Bandeau ton="info" icone={<Lock className="h-5 w-5" aria-hidden />}>
        L’IA n’est pas ouverte à votre compte : la direction l’ouvre en vous accordant un nombre de requêtes par jour.
      </Bandeau>
    );
  }
  if (fonction === 'TUTEUR' && etat.tuteurFerme) {
    return (
      <Bandeau ton="info" icone={<Timer className="h-5 w-5" aria-hidden />}>
        {etat.tuteurFerme.motif}
      </Bandeau>
    );
  }
  if (compteur) {
    const quotaAtteint = compteur.etat === 'QUOTA_ATTEINT';
    return (
      <Bandeau ton={quotaAtteint ? 'danger' : 'neutre'} icone={<Gauge className="h-5 w-5" aria-hidden />}>
        {texteCompteur(compteur)}
        {quotaAtteint && ' — quota atteint : l’IA vous sera de nouveau ouverte demain.'}
      </Bandeau>
    );
  }
  return null;
}

const TONS = {
  info: 'border-amber-200 bg-amber-50 text-amber-900',
  danger: 'border-red-200 bg-red-50 text-red-800',
  neutre: 'border-gray-100 bg-white text-codialis-navy',
} as const;

function Bandeau({
  ton,
  icone,
  children,
}: {
  ton: keyof typeof TONS;
  icone: JSX.Element;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      role="status"
      className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm shadow-card ${TONS[ton]}`}
    >
      <span className="mt-0.5 shrink-0">{icone}</span>
      <p className="min-w-0">{children}</p>
    </div>
  );
}
